
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function SourcingSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredLocation, setHoveredLocation] = useState<string | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const locations = [
    {
      id: 'brazil',
      name: t('brazil'),
      ingredient: t('brazil_ingredient'),
      description: t('brazil_description'),
      image: 'https://public.readdy.ai/ai/img_res/2c5e04490fd50a62fc9b95bcb4e7e431.jpg',
      position: { left: '25%', top: '65%' }
    },
    {
      id: 'peru',
      name: t('peru'),
      ingredient: t('peru_ingredient'),
      description: t('peru_description'),
      image: 'https://readdy.ai/api/search-image?query=Maca%20root%20growing%20in%20Peruvian%20Andes%20mountains%2C%20high%20altitude%20farming%2C%20golden%20yellow%20roots%2C%20traditional%20cultivation%2C%20mountain%20landscape%2C%20sustainable%20agriculture%2C%20ancient%20superfood%2C%20pristine%20environment&width=400&height=300&seq=peru-maca&orientation=landscape',
      position: { left: '20%', top: '70%' }
    },
    {
      id: 'himalaya',
      name: t('himalaya'),
      ingredient: t('himalaya_ingredient'),
      description: t('himalaya_description'),
      image: 'https://readdy.ai/api/search-image?query=Goji%20berries%20growing%20in%20Himalayan%20region%2C%20red%20berries%20on%20bushes%2C%20mountain%20landscape%2C%20high%20altitude%20cultivation%2C%20traditional%20farming%2C%20pristine%20mountain%20environment%2C%20natural%20superfood%20source%2C%20clear%20blue%20sky&width=400&height=300&seq=himalaya-goji&orientation=landscape',
      position: { left: '75%', top: '35%' }
    },
    {
      id: 'mediterranean',
      name: t('mediterranean'),
      ingredient: t('mediterranean_ingredient'),
      description: t('mediterranean_description'),
      image: 'https://readdy.ai/api/search-image?query=Olive%20trees%20in%20Mediterranean%20landscape%2C%20silver-green%20olive%20leaves%2C%20sunny%20hillside%2C%20traditional%20cultivation%2C%20pristine%20natural%20environment%2C%20healthy%20lifestyle%2C%20ancient%20wisdom%2C%20sustainable%20farming&width=400&height=300&seq=mediterranean-olive&orientation=landscape',
      position: { left: '45%', top: '45%' }
    }
  ];

  return (
    <section id="sourcing" ref={sectionRef} className="py-24 lg:py-32 bg-[#F5F1E6]/20">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 
            className="text-xl sm:text-2xl lg:text-4xl font-bold text-[#2A2A2A] mb-6"
            style={{ fontFamily: 'Playfair Display, Noto Serif SC, serif' }}
          >
            {t('sourcing_title')}
          </h2>
          <p 
            className="text-xs sm:text-xs lg:text-base text-[#A6A6A6] max-w-3xl mx-auto"
            style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
          >
            {t('sourcing_subtitle')}
          </p>
        </div>

        {/* Interactive World Map */}
        <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Map Container */}
          <div className="relative bg-white rounded-lg shadow-lg p-8 mb-12">
            <div 
              className="relative w-full h-96 bg-cover bg-center rounded-lg"
              style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Minimalist%20world%20map%20illustration%20in%20soft%20earth%20tones%2C%20clean%20modern%20design%2C%20simple%20continent%20outlines%2C%20cream%20and%20charcoal%20colors%2C%20elegant%20cartography%20style%2C%20suitable%20for%20luxury%20wellness%20brand%2C%20sophisticated%20aesthetic&width=800&height=400&seq=world-map&orientation=landscape')`
              }}
            >
              {/* Location Markers */}
              {locations.map((location) => (
                <div
                  key={location.id}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                  style={{ left: location.position.left, top: location.position.top }}
                  onMouseEnter={() => setHoveredLocation(location.id)}
                  onMouseLeave={() => setHoveredLocation(null)}
                >
                  <div className={`w-1.5 h-1.5 bg-[#2A2A2A] rounded-full transition-all duration-300 ${hoveredLocation === location.id ? 'scale-150 bg-[#F5F1E6]' : ''}`}>
                    <div className="absolute inset-0 bg-[#2A2A2A] rounded-full animate-ping opacity-75"></div>
                  </div>
                  
                  {/* Tooltip */}
                  {hoveredLocation === location.id && (
                    <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-lg p-4 w-64 z-10">
                      <h4 
                        className="text-xs font-semibold text-[#2A2A2A] mb-2"
                        style={{ fontFamily: 'Playfair Display, Noto Serif SC, serif' }}
                      >
                        {location.name} - {location.ingredient}
                      </h4>
                      <p 
                        className="text-xs text-[#A6A6A6]"
                        style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
                      >
                        {location.description}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Location Details */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {locations.map((location, index) => (
              <div 
                key={location.id}
                className={`bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-1000 hover:shadow-xl cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${(index + 1) * 150}ms` }}
                onMouseEnter={() => setHoveredLocation(location.id)}
                onMouseLeave={() => setHoveredLocation(null)}
              >
                <div className="h-48 overflow-hidden">
                  <img 
                    src={location.image}
                    alt={`${location.name} - ${location.ingredient}`}
                    className="w-full h-full object-cover object-top transition-transform duration-300 hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h4 
                    className="text-xs sm:text-sm lg:text-lg font-semibold text-[#2A2A2A] mb-2"
                    style={{ fontFamily: 'Playfair Display, Noto Serif SC, serif' }}
                  >
                    {location.name}
                  </h4>
                  <p 
                    className="text-xs sm:text-xs font-medium text-[#A6A6A6] mb-3"
                    style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
                  >
                    {location.ingredient}
                  </p>
                  <p 
                    className="text-xs text-[#2A2A2A] leading-relaxed"
                    style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
                  >
                    {location.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
